/* eslint-disable @typescript-eslint/naming-convention */
import { getAddressDisplaySchema, removePrefixForPhone } from '../utils/AddressUtils';
import CONSTANTS, { ADDRESS_TYPE_ID, FIELD_TYPE } from '../constants/Constants';
import {
  IAddressResponse,
  INotification,
  IOrderSummaryResponse,
  ITransactionResponse,
  ISiteConfig,
  IUPSValidation,
  IAddressSuggestionResponse,
  ITransPaymentResponse,
  IShippingMethodSummary,
  IBopisInfoResponse,
  IFindSalonsResponse,
  IGetTransactionKeyResponse,
  ISetTransactionKeyResponse,
  IBopisResponseV2,
  ICompanyBillingDetailsResponse,
  IShippingMethodResponse,
  IAcceptedInstallments,
  IConvenienceStoreResponse,
  IError,
  IGiftCardResponse,
  IPaymentSession,
  IShippingMethodsResponse,
  IEstimatedDeliveryResponse,
} from './IApiResponses';
import {
  IAcceptedPrivacyPolicy,
  INewsletterSubscribed,
  IFromAddressMapping,
  IToAddressMapping,
  IFromTransactionMapping,
  GiftCard,
  IFromBopisResponse,
  IFromShippingMethodMapping,
  IBopisData,
  IAPIBopisData,
  ICreditCard,
  IFromBopisInfoResponse,
  IFromFindSalonsResponse,
  IFromSetTransactionKeyResponse,
  IFromGetTransactionKeyResponse,
  ICourierAPIData,
  ICourierPostData,
  ICourierPostAPIResponse,
  ISetCourierResponse,
  IFromCompanyBillingDetailsResponse,
  IToCompanyBillingDetailsRequest,
  ISuggestions,
  IOrderSummary,
  IFromAddressValidationMapping,
  IToCardMessageMapping,
  IFromEstimatedDeliveryMapping,
} from './IDataMappings';
import {
  IConfig,
  CONVENIENCE_STORE,
} from '../domain/entities/Config';
import PAYMENT_PROVIDERS from '../constants/PaymentProviders';
import SHIPPING_METHODS from '../constants/ShippingMethods';
import { IOpeningHours } from '../domain/entities/Store/Store';
import { ISalon } from '../domain/entities/Salon/Salon';
import { ITransactionPayment } from '../domain/entities/Transaction/Transaction';
import { IShippingMethod } from '../domain/entities/ShippingMethodsCollection/ShippingMethodsCollection';
import ConvenienceStore from '../domain/entities/ConvenienceStore/ConvenienceStore';
import { IAddressSchemaProperties } from './JsonSchemaApi';
import dataMappingsModules from './dataMapper';
import { enhancePaymentName } from './dataMapper/transaction/paymentNameEnhance';
import { DELIVERY } from '../routes';
import { INotificationResponse } from './IApiResponses';
import { IGiftMessageData } from './GiftWrapApi';
import Address, { IAddressAttributes } from '../domain/entities/Address';
import GOOGLE_MAP_STYLE from '../constants/GoogleMapStylers';

function zipcodeToUpperCase(value: string): string {
  return value && value.toUpperCase();
}

export function formatSuggestions(
  suggestions: IAddressSuggestionResponse[],
): IAddressSuggestionResponse[] {
  return suggestions.map(suggestion => ({
    ...suggestion,
    zipcode: zipcodeToUpperCase(suggestion.postal_code),
  }));
}

export function formatOrderSummary(orderSummary: IOrderSummaryResponse): IOrderSummary {
  return {
    codSurchargeAmount: orderSummary.cod_surcharge_amount,
    currencyCode: orderSummary.currency_code,
    discount: orderSummary.total_items_discount,
    estimatedDeliveryEndDate: orderSummary.estimated_delivery_end_date,
    estimatedDeliveryStartDate: orderSummary.estimated_delivery_start_date,
    formattedTaxRate: orderSummary.formatted_tax_rate,
    giftCardTotal: orderSummary.gift_card_payment_amount,
    giftWrapTotal: orderSummary.total_giftwrap_applied_price,
    isGiftWrap: orderSummary.giftWrap,
    itemsCount: orderSummary.items_count,
    retailDeliveryFeeMessage: orderSummary.retail_delivery_fee_message,
    rewardPointsPriceInCartFormatted: orderSummary.rewardPointsPriceInCartFormatted,
    subTotal: orderSummary.total_items_base_price,
    subTotalWithoutDonations: orderSummary.subtotal_without_donations,
    taxIsEstimated: !!orderSummary.tax_is_estimated,
    taxRate: orderSummary.tax_rate,
    totalAppliedTax: orderSummary.total_applied_tax,
    totalCost: orderSummary.total_cost,
    totalDonations: orderSummary.total_donations,
    totalIncludedTax: orderSummary.total_included_tax,
    totalRetailDeliveryFee: orderSummary.total_retail_delivery_fee,
    totalShippingPrice: orderSummary.total_shipping_applied_price,
  };
}

function formatBopisInfo(bopisInfo: IBopisInfoResponse): IFromBopisInfoResponse {
  const bopisObj = bopisInfo.bopis || bopisInfo;
  const {
    bopis_is_bopis_or_courier,
    bopis_is_bopis_order,
    bopis_is_courier,
    bopis_purchaser_pickup_info: {
      bopis_email_address,
      bopis_first_name,
      bopis_last_name,
      bopis_phone_number,
    },
    bopis_proxy_pickup_info: {
      proxy_email_address,
      proxy_first_name,
      proxy_last_name,
      proxy_phone,
    },
    bopis_store_number,
  } = bopisObj;

  let notificationsItems: INotification[] = [];
  if (bopisInfo.notifications && bopisInfo.notifications.length) {
    notificationsItems = bopisInfo.notifications.map(item => {
      const {
        key,
        severity,
        prod_rgn_name,
      } = item;

      return {
        key,
        productRgnName: prod_rgn_name,
        severity,
      };
    });
  }

  return {
    doorId: bopis_store_number.toString(),
    isBopisOrCourier: !!bopis_is_bopis_or_courier,
    isBopisOrder: !!bopis_is_bopis_order,
    isCourier: !!bopis_is_courier,
    notifications: notificationsItems,
    proxyPickupInfo: {
      email: proxy_email_address,
      firstName: proxy_first_name,
      lastName: proxy_last_name,
      phone: proxy_phone,
    },
    proxyPickupMode: !!proxy_email_address,
    purchaserPickupInfo: {
      email: bopis_email_address,
      firstName: bopis_first_name,
      lastName: bopis_last_name,
      phone: bopis_phone_number,
    },
  };
}

function formatShippingMethodsData(
  selectedShippingMethodId: SHIPPING_METHODS,
  shippingOptions: IShippingMethodResponse[],
): IShippingMethod[] {
  return dataMappingsModules.deliveryMethodsMapper.response(
    shippingOptions,
    selectedShippingMethodId,
  );
}

function formatCompanyBillingDetails(
  companyBillingData: ICompanyBillingDetailsResponse,
): IFromCompanyBillingDetailsResponse {
  return {
    ...companyBillingData,
    bill_to_company: !!Number(companyBillingData.bill_to_company),
  };
}

function formatBopisV2(resp: IBopisResponseV2): IFromBopisResponse {
  let stores = [];
  if (!resp) {
    return {
      maxRadius: '0',
      stores: [],
    };
  }
  stores = Object.keys(resp.doors).map((key: string) => {
    const {
      ADDRESS: address,
      CITY: city,
      COUNTRY: country,
      DISTANCE: distance,
      DOOR_ID: doorId,
      DOORNAME: name,
      LATITUDE: latitude,
      LONGITUDE: longitude,
      PHONE1: phoneNumber,
      STATE: state,
      SUB_HEADING: subHeading,
      ZIP_OR_POSTAL: zipcode,
      TIME_ZONE: timeZone,
      TIME_ZONE_UTC_OFFSET: timeZoneOffsetSeconds,
    } = resp.doors[key].info;

    const { info } = resp.doors[key];

    const {
      courier_delivery: courierDelivery,
    } = resp.doors[key].omni_door_status;

    return {
      address,
      city,
      country,
      courierDelivery,
      distance: Number(distance),
      doorId,
      info,
      inventory: [],
      latitude,
      longitude,
      name,
      openingHours: [],
      phoneNumber,
      state,
      subHeading,
      timeZone,
      timeZoneOffsetSeconds,
      zipcode: zipcodeToUpperCase(zipcode),
    };
  });

  let storesHours: { id: string; hours: IOpeningHours[] }[] = [];
  storesHours = Object.keys(resp.doors).map((storeKey: string) => {
    const hoursRaw = resp.doors[storeKey].store_hours
      && resp.doors[storeKey].store_hours.raw;
    const hours = hoursRaw && Object.keys(hoursRaw).map((key: string) => {
      const {
        open: openingHours,
        close: closedHours,
      } = hoursRaw[Number(key)];
      return {
        closedHours: Number(closedHours),
        day: Number(key),
        openingHours: Number(openingHours),
      };
    });

    return {
      hours,
      id: storeKey,
    };
  });

  let workingTime: { id: string; time: string[] }[] = [];
  workingTime = Object.keys(resp.doors).map((storeKey: string) => {
    const workingTimeRendered = resp.doors[storeKey].store_hours
      && resp.doors[storeKey].store_hours.rendered;
    const time = workingTimeRendered && Object.keys(workingTimeRendered).map((key: string) => {
      return workingTimeRendered[Number(key)];
    });

    return {
      id: storeKey,
      time,
    };
  });

  let storeInventory: {
    id: string;
    inventory: {
      availableQuantity: number;
      isAvailable: number;
      skuBaseId: number;
    }[];
  }[] = [];
  storeInventory = Object.keys(resp.doors).map((storeKey: string) => {
    const inventoryRaw = resp.doors[storeKey].door_inventory.skus_onhand;
    const inventory = Object.keys(inventoryRaw).map((key: string) => {
      const {
        available_qty: availableQuantity,
        is_available: isAvailable,
        is_free_item: isFreeItem,
        is_gwp_offer: isGWPOfferItem,
        is_sample_item: isSampleItem,
        gwp_saleable_sku: GWPSaleableSku,
      } = inventoryRaw[key];
      return {
        GWPSaleableSku: Number(GWPSaleableSku),
        availableQuantity,
        isAvailable,
        isFreeItem,
        isGWPOfferItem,
        isSampleItem,
        skuBaseId: Number(key),
      };
    });

    return { id: storeKey, inventory };
  });
  const { courier_door_selected, config } = resp;
  stores = stores.map(store => {
    const { doorId } = store;
    return {
      ...store,
      courierRadius: config?.courier_radius,
      inventory: storeInventory.find(el => el.id === doorId).inventory,
      isCourierSelected: store.doorId === courier_door_selected,
      openingHours: storesHours.find(el => el.id === doorId).hours,
      unitOfMeasure: config?.uom,
      workingTime: workingTime.find(el => el.id === doorId).time,
    };
  });
  const maxRadius = resp.max_radius || '0';
  return { maxRadius, stores };
}

function formatConvenienceStoreInfo(
  convenienceStoreResp: IConvenienceStoreResponse,
): ConvenienceStore {
  const {
    address, name, phone, postcode, type,
  } = convenienceStoreResp;
  return {
    fullAddress: address,
    isReady: true,
    phoneNumber: phone,
    postalCode: postcode,
    storeName: name,
    storeType: type || CONVENIENCE_STORE.SAGAWA,
  };
}

const DataMappings = {

  fromAcceptedPrivacyPolicy: {
    response(resp: { accepted_privacy_policy: boolean }): IAcceptedPrivacyPolicy {
      const {
        accepted_privacy_policy: acceptedPrivacyPolicy,
      } = resp;

      return {
        acceptedPrivacyPolicy: !!acceptedPrivacyPolicy,
      };
    },
  },

  fromAddGiftCard: {
    response(resp: IGiftCardResponse): GiftCard {
      const giftCard = resp.trans_payments.find(
        (payment: ITransPaymentResponse) => payment.pp_name === CONSTANTS.GIFT_CARD,
      );

      return {
        amount: giftCard.payment_amount,
        balance: giftCard.balance,
        id: giftCard.trans_payment_id,
        number: giftCard.gc_giftcard_number,
        paymentMethods: resp.payment_init?.[0] || null,
        paymentSession: (resp.payment_init?.[0] as IPaymentSession)?.paymentSession || null,
      };
    },
  },

  fromAddressValidation: {
    response(resp: {
      valid?: boolean,
      errors?: IError[],
    }): IFromAddressValidationMapping {
      return {
        errors: resp.errors,
        valid: !!resp.valid,
      };
    },
  },

  fromBopisInfo: {
    response(data: IBopisInfoResponse): IFromBopisInfoResponse {
      return formatBopisInfo(data);
    },
  },

  fromCompanyBillingDetailsInfo: {
    response(
      data: ICompanyBillingDetailsResponse,
    ): IFromCompanyBillingDetailsResponse {
      return {
        [FIELD_TYPE.BILL_TO_COMPANY]: !!data.bill_to_company,
        [FIELD_TYPE.COMPANY_ADDRESS]: data.company_address,
        [FIELD_TYPE.COMPANY_NAME]: data.company_name,
        [FIELD_TYPE.COMPANY_TAX_NUMBER]: data.company_tax_number,
      };
    },
  },

  fromEstimatedDelivery: {
    response(resp: IEstimatedDeliveryResponse): IFromEstimatedDeliveryMapping {
      const {
        cart_items: transCartItems,
        ship_methods,
        ship_options,
        order_summary, 
        order_ship_method,
      } = resp;
      const shippingMethods = ship_methods
        && formatShippingMethodsData(Number(order_ship_method) as SHIPPING_METHODS, ship_methods);
      const cartItems = dataMappingsModules.cartItemsMapper.response({
        cartItems: transCartItems,
        transactionOrder: resp.trans_order,
      });
      const orderSummary = formatOrderSummary(order_summary);
      const shippingOptions = dataMappingsModules.shipOptionsMapper.response(
        ship_options,
      );

      return {
        cartItems,
        orderSummary,
        shippingMethods,
        shippingOptions,
      };
    },
  },

  fromFindSalons: {
    response(resp: IFindSalonsResponse): IFromFindSalonsResponse {
      const salons: ISalon[] = [];
      const {
        data: {
          doors: {
            results,
          },
        },
      } = resp;
      Object.keys(results).forEach((id: string) => {
        const {
          ADDRESS: doorAddress,
          CITY: doorCity,
          SALON_ID: salonId,
          DOORNAME: doorName,
          STATE_OR_PROVINCE: doorState,
        } = results[id];
        salons.push({
          doorAddress,
          doorCity,
          doorName,
          doorState,
          salonId,
        });
      });

      return {
        salons,
      };
    },
  },

  fromGetBopisToStores: {
    response(resp: IBopisResponseV2): IFromBopisResponse {
      try {
        return formatBopisV2(resp as IBopisResponseV2);
      } catch (e) {
        return {
          maxRadius: resp?.max_radius || '0',
          stores: [],
        };
      }
    },
  },

  fromGetSuggestionAddresses: {
    response(resp: { suggestions: IAddressResponse[] }): ISuggestions {
      return {
        suggestions: resp.suggestions.map(suggestion => dataMappingsModules.addressMapper.response(suggestion)),
      };
    },
  },

  fromGetTransactionKey: {
    response(data: IGetTransactionKeyResponse): IFromGetTransactionKeyResponse {
      return {
        transKey: data.trans_key,
      };
    },
  },

  fromSetCourier: {
    response(data: ICourierPostAPIResponse): ISetCourierResponse {
      return {
        isAddressUndeliverable: !data.courier_mode_success,
      };
    },
  },

  fromSetTransactionKey: {
    response(data: ISetTransactionKeyResponse): IFromSetTransactionKeyResponse {
      return {
        validatedTrans: data.validated_trans,
      };
    },
  },

  fromShipMethod: {
    response(resp: IShippingMethodSummary): IFromShippingMethodMapping {
      return {
        orderSummary: formatOrderSummary(resp.order_summary),
        transId: resp.trans_id,
      };
    },
  },

  fromShipMethods: {
    response(resp: IShippingMethodsResponse): IShippingMethod[] {
      return dataMappingsModules.deliveryMethodsMapper.response(
        resp.ship_methods,
        Number(resp.order_ship_method) as SHIPPING_METHODS,
      );
    },
  },

  fromSiteConfig: {
    response(resp: ISiteConfig): IConfig {
      const {
        address_config,
        shipping_address_input_schema,
        billing_address_input_schema,
        contact_info_input_schema,
        packstation_address_input_schema,
        base_url: baseUrl,
        brand_id: brandId,
        brand_name: brandName,
        region_data: {
          billing_countries: deprecatedBillingCountries,
          allowed_billing_countries: billingCountries,
          shipping_countries: deprecatedShippingCountries,
          allowed_shipping_countries: shippingCountries,
          allowed_packstation_countries: packstationCountries,
          states,
          select_cities: cities,
        },
        region_id: regionId,
        region_name: regionName,
        country_id: newCountryId,
        currency,
        courier,
        locale,
        payment_instructions,
        site_config,
        site_config: {
          accepted_privacy_policy,
          backend_validation,
          adv_legal: advancedLegal,
          allow_gc_without_cc,
          checkout_idle_timeout: sessionTimeout,
          checkout_use_new_design: useNewAddressDesign,
          is_melissa_enabled: isMelissaEnabled,
          is_production: isProduction,
          use_ups_validation_at_checkout: useUpsValidation,
          use_city_state_lookup: useCityStateLookup,
          use_shipping_calculator: useShippingCalculator,
          tnc_content_node_id: tncContentNodeId,
          klarna_popup_node_id: klarnaContentNodeId,
          //TODO: gift_wrap - https://jira.esteeonline.com/browse/UKPRGT-3368
          giftwrap: giftWrap,
          show_free_label_as_price: showFreeLabelAsPrice,
          show_strikeoff_price: showStrikeoffPrice,
          display_included_tax: displayIncludedTax,
          country_taxes_label: countryTaxesLabel,
          use_json_schemas_for_address_validation: jsonSchemasEnabled,
          display_shipping_fee_with_tax: displayShippingFeeWithTax,
          has_vat: vatPercentage,
          max_gift_card_number_length: maxGiftCardNumberLength,
          privacy_policy_url: privacyPolicyUrl,
          google_maps_key: googleMapsKey,
          use_e164_phone_standard: useE164PhoneStandard,
          enable_donation: isCharityDonationEnabled,
          enable_wallet_payment: isWalletPaymentEnabled,
        },
        display_store_collection: displayStoreCollection,
        rco_ups_enabled: isClickAndCollect,
        rco_bopis_enabled: bopisEnabled,
        rco_bopis_gift_wrap_enabled: bopisGiftWrapEnabled,
        rco_courier_enabled: isSameDayEnabled,
        rco_salon_enabled: salonCommissionEnabled,
        salon_data_share_enabled: isSalonDataShareEnabled,
        // async_address: condensedAddressEnabled, RCO-7996 - Disabling limited view of Addresses
        condensed_fields: condensedFields,
        rco_edd_enabled: isEstimatedDeliveryDateEnabled,
        rco_split_shipment_enabled: isSplitShipmentEnabled,
        newsletter,
        stardust,
        bill_to_company,
        bill_to_company_schema,
        static_lookup_urls,
        use_generic_address_handler: useGenericAddressHandler,
        display_our_guarantee_on,
        display_store_name_with_address,
        routes,
        country_phone_verification_schema,
        google_map_style,
      } = resp;

      const countryId = newCountryId || site_config.site.country_id;

      // remove deprecated properties when safe
      let shippingAddressInputSchema;
      if (shipping_address_input_schema) {
        shippingAddressInputSchema = shipping_address_input_schema.properties;
      } else if (address_config) {
        const {
          shipping_address_input_schema: deprecatedShippingSchema,
        } = address_config;
        shippingAddressInputSchema = deprecatedShippingSchema.properties
          || deprecatedShippingSchema.address.properties;
      }

      // remove deprecated propeties when safe
      const billingAddressInputSchema = billing_address_input_schema
        ? billing_address_input_schema.properties
        : address_config.billing_address_input_schema.properties;

      const packstationAddressInputSchema = packstation_address_input_schema
        ? packstation_address_input_schema.properties
        : null;

      const contactInfoInputSchema = contact_info_input_schema?.properties;

      const billingAddressDisplaySchema = getAddressDisplaySchema(billingAddressInputSchema);
      const shippingAddressDisplaySchema = getAddressDisplaySchema(shippingAddressInputSchema);

      // remove deprecated properties when safe
      // eslint-disable max-len
      const defaultTermsConditions = accepted_privacy_policy?.default_terms_conditions;

      // remove deprecated properties when safe
      const messageOnlyTermsConditions = accepted_privacy_policy?.message_only_terms_conditions;

      // eslint-enable max-len

      // remove deprecated properties when safe
      const canCheckoutWithoutPayment = Boolean(allow_gc_without_cc);

      const isSameDayDeliveryAvailable = courier && !!isSameDayEnabled;

      const {
        businessUnitId,
        clientId,
        defaultDomain,
        environment,
      } = { ...stardust };

      // Check to see if company billing is activated
      const canSetCompanyBillingEnabled = !!bill_to_company;

      const billToCompanySchema = bill_to_company_schema
        ? bill_to_company_schema.properties
        : null;

      const companyBillingDisplaySchema = getAddressDisplaySchema(
        billToCompanySchema,
      );

      // Will handle showing the OurGuarantee section on both delivery and payment page
      // By default, the section should be displayed on the delivery_page
      const displayOurGuaranteeOn = {
        deliveryPage: display_our_guarantee_on ? !!display_our_guarantee_on.delivery_page : true,
        paymentPage: !!display_our_guarantee_on?.payment_page || false,
      };

      return {
        advancedLegal: !!advancedLegal,
        backendValidation: {
          countryId: backend_validation?.country_id,
          shouldValidateSavedShippingAddress: backend_validation?.should_validate_saved_shipping_address,
          validationType: backend_validation?.validation_type,
        },
        baseUrl: baseUrl || window.location.origin,
        billToCompanySchema,
        billingAddressDisplaySchema,
        billingAddressInputSchema,
        billingCountries: billingCountries || deprecatedBillingCountries,
        bopisEnabled: !!bopisEnabled,
        bopisGiftWrapEnabled: !!bopisGiftWrapEnabled,
        brandId,
        brandName,
        canCheckoutWithoutPayment,
        canSetCompanyBilling: canSetCompanyBillingEnabled,
        cities,
        companyBillingDisplaySchema,
        condensedAddressEnabled: false,
        condensedFields,
        contactInfoInputSchema,
        countryId,
        countryTaxesLabel: !!countryTaxesLabel,
        currency: currency || site_config.site.currency,
        defaultTermsConditions: !!defaultTermsConditions,
        displayIncludedTax: !!displayIncludedTax,
        displayOurGuaranteeOn,
        displayShippingFeeWithTax: !!displayShippingFeeWithTax,
        displayStoreCollection: !!displayStoreCollection,
        externalRoutes: routes,
        giftWrap: !!giftWrap,
        googleMapStyle: google_map_style as GOOGLE_MAP_STYLE,
        googleMapsKey,
        isCharityDonationEnabled: !!isCharityDonationEnabled,
        isClickAndCollect: !!isClickAndCollect,
        isEstimatedDeliveryDateEnabled: !!isEstimatedDeliveryDateEnabled,
        isMelissaEnabled: !!isMelissaEnabled,
        isProduction: !!isProduction,
        isSalonDataShareEnabled: !!isSalonDataShareEnabled,
        isSameDayDeliveryAvailable,
        isSplitShipmentEnabled: !!isSplitShipmentEnabled,
        isWalletPaymentEnabled: !!isWalletPaymentEnabled,
        jsonSchemasEnabled: !!jsonSchemasEnabled,
        klarnaContentNodeId,
        locale,
        maxGiftCardNumberLength,
        messageOnlyTermsConditions: !!messageOnlyTermsConditions,
        newsletter,
        packstationAddressInputSchema,
        packstationCountries,
        paymentInstructions: Boolean(payment_instructions),
        phonePatterns: country_phone_verification_schema,
        privacyPolicyUrl,
        regionId,
        regionName,
        salonCommissionEnabled: !!salonCommissionEnabled,
        sessionTimeout,
        shippingAddressDisplaySchema, // remove deprecated code when it's safe
        shippingAddressInputSchema,
        shippingCountries: shippingCountries || deprecatedShippingCountries,
        showFreeLabelAsPrice: !!showFreeLabelAsPrice,
        showStoreNameWithAddress: !!display_store_name_with_address,
        showStrikeoffPrice: !!showStrikeoffPrice,
        stardustServiceConfig: {
          baseUrl: defaultDomain,
          businessUnitId,
          clientId,
          environment,
        },
        states,
        staticDataLookupUrls: static_lookup_urls,
        tncContentNodeId,
        useCityStateLookup: !!useCityStateLookup,
        useE164PhoneStandard: !!useE164PhoneStandard,
        useGenericAddressHandler: !!useGenericAddressHandler,
        useNewAddressDesign: !!useNewAddressDesign,
        useShippingCalculator: !!useShippingCalculator,
        useUpsValidation: !!useUpsValidation,
        vatPercentage,
      };
    },
  },

  fromSubscription: {
    response(resp: { newsletter_signup: number }): INewsletterSubscribed {
      const { newsletter_signup: newsletterSubscribed } = resp;
      return {
        newsletterSubscribed: !!newsletterSubscribed,
      };
    },
  },

  fromSuggestions: {
    response(resp: IUPSValidation): IUPSValidation {
      return {
        indicators: resp.indicators,
        suggestions: formatSuggestions(resp.suggestions),
      };
    },
  },

  fromTransaction: {
    response(resp: ITransactionResponse): IFromTransactionMapping {
      const {
        cart_items: transCartItems,
        named_delivery_date_time: {
          delivery_date: deliveryDate,
          delivery_time: deliveryTime,
        } = {},
        notifications,
        order_summary: transOrderSummary,
        offers_applied: appliedOffers,
        user,
        user: {
          email_address: guestUserEmail,
          is_guest_checkout: isGuestUser,
          saved_cc_payments: savedCreditCardsItems,
          loyalty_active_flag: userHasLoyaltyEnabled,
        },
        trans_order: {
          trans_id: transId,
          trans_order_id: transOrderId,
          loyaltyPoints,
          card_message: cardMessage,
          giftWrap: giftWrap,
        },
        trans_key: transKey,
        trans_payments,
        payment_options: paymentOptions,
        order_ship_method,
        salon,
        // ship_methods are dynamic depending on UI state, ship_options are static ship methods.
        ship_methods,
        ship_options,
        errors,
        bopis,
        convenience_store,
        bill_to_company,
        installments,
        delivery_tabs_disabled,
        is_POA,
        delivery_options,
        is_final_sale,
      } = resp;
      // shipping address
      let address;
      let userAddresses: IAddressAttributes[] = [];
      let userShippingAddresses: IAddressAttributes[] = [];

      const isProCustomer = !!(user.pro_info && user.pro_info.isPro);

      if (user.addresses) {
        const { addresses } = user;

        if (addresses.length) {
          userAddresses = addresses.map(userAddress => dataMappingsModules.addressMapper.response(userAddress));
        }
      }
      if (user.shipping_addresses) {
        const { shipping_addresses } = user;

        if (shipping_addresses && shipping_addresses.length) {
          userShippingAddresses = shipping_addresses
            .map(userAddress => dataMappingsModules.addressMapper.response(userAddress));
        }
      }

      if (resp.order_ship_address?.address) {
        const {
          order_ship_address: {
            address: shippingAddress,
          },
        } = resp;
        address = shippingAddress;
      }

      // all offers applied to total (manual and automatic)
      const allAppliedOffers = (appliedOffers && appliedOffers.length)
        ? appliedOffers.map(item => {
          const {
            offer_code: offerCode,
            confirm_message: confirmMsg,
            allow_auto_messages: allowMsg,
            ...common
          } = item;

          return {
            allowMsg,
            confirmMsg,
            offerCode,
            ...common,
          };
        })
        : [];

      const cartItems = dataMappingsModules.cartItemsMapper.response({
        cartItems: transCartItems,
        transactionOrder: resp.trans_order,
      });
      const orderSummary = formatOrderSummary(transOrderSummary);

      const shippingAddress = address ? dataMappingsModules.addressMapper.response(address) : undefined;

      const billingAddress = trans_payments.length && trans_payments[0].billing_address
        && trans_payments[0].billing_address
        ? dataMappingsModules.addressMapper.response(trans_payments[0].billing_address) : undefined;
      const isExpressFlow = trans_payments.length
        ? Boolean(trans_payments[0].is_ready_to_complete)
        : false;

      const startScreen = trans_payments && trans_payments[0]?.start_screen || DELIVERY.ROUTE;

      let selectedPayment;
      let selectedPaymentMode;
      const giftCards: GiftCard[] = [];
      trans_payments.forEach((p: ITransPaymentResponse) => {
        if (p.pp_name !== CONSTANTS.GIFT_CARD) {
          selectedPayment = enhancePaymentName(p.pp_name, p.payment_mode, p.payment_type);
          selectedPaymentMode = p.payment_mode;
        } else {
          giftCards.push({
            amount: p.payment_amount,
            balance: p.balance,
            id: p.trans_payment_id,
            number: p.gc_giftcard_number,
          });
        }
      });
      const isAfterpayEligible = paymentOptions
        ? paymentOptions.some(
          p => p.pp_name === PAYMENT_PROVIDERS.AFTERPAY
            && Boolean(p.is_available),
        )
        : false;

      const isAfterpay = paymentOptions
        ? paymentOptions.some(
          p => p.pp_name === PAYMENT_PROVIDERS.AFTERPAY,
        )
        : false;

      let afterpayExcludedProducts = false;

      if (isAfterpay) {
        const afterPay = paymentOptions.find(p => p.pp_name === PAYMENT_PROVIDERS.AFTERPAY);
        afterpayExcludedProducts = afterPay.key.some(k => k === 'after_pay_error_excluded_products');
      }

      const shippingMethods = ship_methods
        && formatShippingMethodsData(Number(order_ship_method) as SHIPPING_METHODS, ship_methods);
      // user
      let userEmail;
      let userId;

      // TODO: (user.user) is deprecated. Remove when RCO-1901 is in production
      const userEntity: ITransactionResponse['user'] = user.user || user;

      if (!userEntity.is_guest_checkout) {
        const {
          email_address: registeredUserEmail,
          user_id,
        } = userEntity;
        userEmail = registeredUserEmail;
        userId = user_id;
      } else {
        userEmail = guestUserEmail;
      }
      const registered = userEntity && userEntity.registered;
      const recognized = userEntity && userEntity.recognized;

      let notificationsItems: INotification[] = [];
      if (notifications && notifications.length) {
        notificationsItems = notifications.map(item => {
          if (item.data) {
            const {
              key,
              severity,
              prod_rgn_name,
              data,
              data: {
                offer_code: offerRemoved,
              },
            } = item;

            return {
              data,
              key,
              offerRemoved,
              productRgnName: prod_rgn_name,
              severity,
            };
          }

          const {
            key,
            severity,
            prod_rgn_name,
          } = item;

          return {
            key,
            productRgnName: prod_rgn_name,
            severity,
          };
        });
      }

      let savedCreditCards: ICreditCard[] = [];
      if (savedCreditCardsItems && savedCreditCardsItems.length) {
        savedCreditCards = savedCreditCardsItems.map(item => {
          const {
            billing_address_id,
            card_type,
            default_payment_method,
            displaycardnum,
            expired,
            expires,
            cc_user_payment_method_id,
          } = item;
          return {
            billingAddressId: billing_address_id,
            displayCardNumber: displaycardnum,
            expires,
            isDefault: default_payment_method,
            isExpired: !!expired,
            type: card_type,
            userPaymentMethodId: cc_user_payment_method_id,
          };
        });
      }

      let transactionPayments: Partial<ITransactionPayment>[] = [];
      if (trans_payments && trans_payments.length) {
        transactionPayments = trans_payments.map(item => {
          const {
            balance,
            billing_address_id,
            gc_giftcard_number,
            is_ready_to_complete,
            payment_amount,
            payment_mode,
            payment_type,
            pp_name,
            restrict_card_number,
            trans_payment_id,
          } = item;
          return {
            balance,
            billingAddressId: billing_address_id,
            giftcardNumber: gc_giftcard_number,
            id: trans_payment_id,
            isReadyToComplete: is_ready_to_complete,
            paymentAmount: Number(payment_amount),
            paymentName: enhancePaymentName(pp_name, payment_mode, payment_type),
            paymentType: payment_type,
            restrictCardNumber: restrict_card_number,
          };
        });
      }

      let bopisInfo;
      if (bopis && bopis.bopis_is_bopis_order) {
        // TODO remove conversion to unknown after BE changes
        bopisInfo = formatBopisInfo(resp as unknown as IBopisInfoResponse);
      }

      let convenienceStore;
      if (convenience_store) {
        convenienceStore = formatConvenienceStoreInfo(convenience_store);
      }

      let billToCompany;
      if (bill_to_company) {
        billToCompany = formatCompanyBillingDetails(bill_to_company);
      }

      const paymentProviders = dataMappingsModules.paymentOptionsMapper.response(paymentOptions);
      const acceptedInstallments: IAcceptedInstallments[] = [];
      if (installments && installments.length) {
        installments.forEach((installment: IAcceptedInstallments) => {
          acceptedInstallments.push(installment);
        });
      }
      const userSalon = salon && dataMappingsModules.salonMapper.response(salon);

      const shippingOptions = dataMappingsModules.shipOptionsMapper.response(
        ship_options,
      );

      const giftMessageSettings = {
        giftMessageCharLimit: resp.gift_message?.char_limit,
        giftMessageEnabled: !!resp.gift_message?.enabled,
        giftMessagePrice: resp.gift_message?.gm_sku_price,
      };

      const giftWrapSettings = {
        giftWrapDiscountPrice: resp.gift_wrap?.gw_sku_discount_price,
        giftWrapEnabled: !!resp.gift_wrap?.enabled,
        giftWrapPrice: resp.gift_wrap?.gw_sku_price,
        giftWrapSelectedSku: resp.gift_wrap?.gw_selected_sku,
        giftWrapSkus : resp.gift_wrap?.gw_skus,
        isDefaultGWSelected: !!resp.gift_wrap?.default_gw_selected,
        isGWImageHiddenInShoppingBag: !!resp.gift_wrap?.hide_shopping_bag_gw_sku,
        isGiftWrapImageHidden: !!resp.gift_wrap?.hide_gw_image,
        isGiftWrapMultipleEnabled: !!resp.gift_wrap?.multiple_giftwraps,
      };

      const deliveryOptions = dataMappingsModules.deliveryOptionsMapper.response(delivery_options);

      return {
        acceptedInstallments,
        afterpayExcludedProducts,
        allAppliedOffers,
        billToCompany,
        billingAddress,
        bopisInfo,
        cardMessage,
        convenienceStore,
        deliveryDate,
        deliveryOptions,
        deliveryTabsDisabled: !!delivery_tabs_disabled,
        deliveryTime,
        errors,
        firstName: user.first_name,
        giftCards,
        giftMessageSettings,
        giftWrap: !!giftWrap,
        giftWrapSettings,
        isAfterpayEligible,
        isExpressFlow,
        isFinalSale: !!is_final_sale,
        isGuestUser,
        isKeptSignedIn: user.iskeptsignedin,
        isPOA: !!is_POA,
        isProCustomer: !!isProCustomer,
        lastName: user.last_name,
        loyaltyPoints,
        nationalId: String(user.national_id),
        notifications: notificationsItems,
        order: {
          cartItems,
          transOrderId,
        },
        orderSummary,
        paymentProviders,
        recognized,
        registered,
        savedCreditCards,
        selectedPayment,
        selectedPaymentMode,
        selectedShippingMethodId: (Number(order_ship_method) as SHIPPING_METHODS),
        shippingAddress,
        shippingMethods,
        shippingOptions,
        startScreen,
        transId,
        transKey,
        transactionPayments,
        userAddresses,
        userEmail,
        userHasLoyaltyEnabled: !!userHasLoyaltyEnabled,
        userId,
        userSalon,
        userShippingAddresses,
      };
    },
  },

  fromWithAddress: {
    response(resp: {
      address: IAddressResponse,
      errors: IError[],
      notifications: INotificationResponse[],
    }): IFromAddressMapping {
      return {
        address: dataMappingsModules.addressMapper.response(resp.address),
        errors: resp.errors,
        notifications: resp.notifications,
      };
    },
  },

  toAddress: {
    request(address: IAddressAttributes & { type: string, useE164PhoneStandard: boolean }): IToAddressMapping {
      const default_ship: number = address.default_shipping ? 1 : 0;
      const {
        phone1,
        phone2,
        phone2_type,
        address_id,
        delivery_instructions,
        address_type,
        default_shipping,
        created_date,
        updated_date,
        ...rest
      } = address;

      let modifiedPhone1 = phone1;
      let modifiedPhone2 = phone2;

      if (!address.useE164PhoneStandard) {
        modifiedPhone1 = removePrefixForPhone(
          phone1,
          address.country_id,
        );
        if (phone2) {
          modifiedPhone2 = removePrefixForPhone(
            phone2,
            address.country_id,
          );
        }
      }

      const isBilling = address.type === CONSTANTS.BILLING_ADDRESS;

      return {
        address: {
          ...(!isBilling && {
            address_id,
            default_shipping: default_ship,
            delivery_instructions,
            ship_type: 1,
          }),
          // Only pass address_type for new address that is not shipping (default).
          // If address in edit mode not pass this parameter.
          ...(Address.isNewAddress(address_id) && address_type && address_type !== ADDRESS_TYPE_ID.SHIPPING && {
            address_type,
          }),
          phone1: modifiedPhone1,
          ...(phone2 && {
            phone2: modifiedPhone2,
            phone2_type,
          }),
          createdDate: created_date,
          updatedDate: updated_date,
          ...rest,
        },
      };
    },
  },

  toAddressSchema: {
    response(addressSchema: IAddressSchemaProperties): IAddressSchemaProperties {
      return addressSchema;
    },
  },

  toBopisInfo: {
    request(info: IBopisData): IAPIBopisData {
      const {
        doorId,
        purchaserPhone,
        purchaserEmail,
        purchaserLastName,
        purchaserFirstName,
        proxyMode,
        proxyPhone,
        proxyEmail,
        proxyLastName,
        proxyFirstName,
      } = info;

      let proxy = {};

      if (proxyMode) {
        proxy = {
          proxy_bopis_email: proxyEmail,
          proxy_bopis_first_name: proxyFirstName,
          proxy_bopis_last_name: proxyLastName,
          proxy_bopis_phone_number: proxyPhone,
        };
      }

      return {
        door_id: doorId,
        door_mode: 'set',
        no_address_delete: 0,
        no_cart_delete: 0,
        proxy_pickup_mode: proxyMode ? 'set' : 'unset',
        purchaser_bopis_email: purchaserEmail,
        purchaser_bopis_first_name: purchaserFirstName,
        purchaser_bopis_last_name: purchaserLastName,
        purchaser_bopis_phone_number: purchaserPhone,
        purchaser_pickup_mode: 'set',
        ...proxy,
      };
    },
  },

  toCardMessage: {
    request(request : IGiftMessageData): IToCardMessageMapping {
      const {
        cardMessageTo,
        cardMessageFrom,
        cardMessage,
        transId,
      } = request;

      return {
        card_message: cardMessage,
        card_message_from: cardMessageFrom,
        card_message_to: cardMessageTo,
        trans_id: transId,
      };
    },
  },

  toCompanyBillingDetailsRequest: {
    request(
      request: IFromCompanyBillingDetailsResponse & { type: string },
    ): IToCompanyBillingDetailsRequest {
      return {
        bill_to_company: {
          ...request,
          bill_to_company: Number(request.bill_to_company),
        },
      };
    },
  },

  toCourierInfo: {
    request(info: ICourierPostData): ICourierAPIData {
      const {
        addressId,
        doorId,
        dropoffNotes,
        dropoffDeadlineTime,
        dropoffReadyTime,
        pickupDeadlineTime,
        pickupReadyTime,
      } = info;

      return {
        courier_mode: 'set',
        door_id: doorId,
        dropoff_address_id: addressId,
        dropoff_deadline_dt: dropoffDeadlineTime || '',
        dropoff_notes: dropoffNotes,
        dropoff_ready_dt: dropoffReadyTime || '',
        pickup_deadline_dt: pickupDeadlineTime,
        pickup_ready_dt: pickupReadyTime,
      };
    },
  },
  toSelectedAddressFields: {
    response(resp: { suggestions: IAddressResponse[] } ): IAddressAttributes {
      const { suggestions } = resp;
      const addressFields = suggestions.length ? suggestions[0] : null;
      return dataMappingsModules.addressMapper.response(addressFields);
    },
  },
};

// this file is way to big, lets gradually move each mapper into its own file
export default { ...DataMappings, ...dataMappingsModules };